import { type ReactNode, useEffect, useRef, useState } from "react";

export type SegmentedControlOption<T extends string = string> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

export type SegmentedControlSize = "sm" | "md" | "lg";

export type SegmentedControlProps<T extends string = string> = {
  options: SegmentedControlOption<T>[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  size?: SegmentedControlSize;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
};

const sizeStyles = {
  sm: "h-6 text-xs px-3",
  md: "h-10 text-sm px-5",
  lg: "h-12 text-base px-6",
};

export function SegmentedControl<T extends string = string>({
  options,
  value,
  defaultValue,
  onChange,
  size = "md",
  fullWidth = false,
  disabled = false,
  className = "",
}: SegmentedControlProps<T>) {
  const [internalValue, setInternalValue] = useState<T>(defaultValue ?? options[0]?.value);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const activeIndex = options.findIndex((opt) => opt.value === currentValue);
    const buttons = containerRef.current.querySelectorAll("button");
    const activeButton = buttons[activeIndex];

    if (activeButton) {
      setIndicatorStyle({
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      });
    }
  }, [currentValue, options]);

  const handleSelect = (optionValue: T) => {
    if (disabled) return;
    if (!isControlled) {
      setInternalValue(optionValue);
    }
    onChange?.(optionValue);
  };

  return (
    <div
      ref={containerRef}
      className={`
        relative inline-flex items-center p-1 rounded-lg bg-surface-900 border border-surface-700
        ${fullWidth ? "w-full" : ""}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        ${className}
      `}
    >
      {/* Sliding indicator - subtle like Tabs */}
      <div
        className="absolute top-1 bottom-1 rounded-md bg-brand-500/10 transition-all duration-200 ease-out"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
      />

      {options.map((option) => {
        const isActive = option.value === currentValue;
        const isDisabled = disabled || option.disabled;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => !isDisabled && handleSelect(option.value)}
            disabled={isDisabled}
            className={`
              relative z-10 flex items-center justify-center font-bold rounded-md
              transition-colors duration-200
              ${sizeStyles[size]}
              ${fullWidth ? "flex-1" : ""}
              ${isActive ? "text-brand-500" : "text-surface-400 hover:text-surface-200"}
              ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
